import * as React from 'react';
import { cn } from '../../lib/cn';
import { segmented_target_index } from './segmented_control_keys';

/**
 * Single choice between a few mutually exclusive options, shown as one row of
 * pill segments — the design system's "Button group · segmented control"
 * (`docs/design-system/index.html` `#buttons`): `.nv-tab-pill` segments on a
 * `.glass-soft` track.
 *
 * Use this for picking a SETTING (a language, a view density). It is a
 * radio group, not navigation: the selected segment is `aria-checked`, never
 * `aria-current="page"` — for navigation use `<TabPill>` or `<Tabs>`.
 *
 * Keyboard follows the WAI-ARIA radio-group pattern: the group is one tab
 * stop (the selected segment); the arrow keys (mirrored in RTL), Home and End
 * move AND select.
 */
export interface SegmentedControlOption {
  value: string;
  label: React.ReactNode;
}

export interface SegmentedControlProps {
  options: readonly SegmentedControlOption[];
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name of the group (required unless `aria-labelledby` is set). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** `fill` stretches the segments to share the full width (phone rows). */
  fill?: boolean;
  className?: string;
}

export function SegmentedControl({
  options,
  value,
  onValueChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  fill = false,
  className,
}: SegmentedControlProps): React.ReactElement {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const selected_index = options.findIndex((o) => o.value === value);
  // A group whose value matches nothing still needs one tab stop.
  const focusable_index = selected_index === -1 ? 0 : selected_index;

  const select = (index: number): void => {
    const option = options.at(index);
    if (option === undefined) return;
    onValueChange(option.value);
    refs.current[index]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={cn(
        'glass-soft rounded-lg p-1 gap-1',
        fill ? 'flex w-full' : 'inline-flex',
        className,
      )}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={index === focusable_index ? 0 : -1}
            className={cn(
              'nv-tab-pill',
              checked && 'is-active',
              fill && 'flex-1',
            )}
            onClick={() => {
              if (!checked) onValueChange(option.value);
            }}
            onKeyDown={(event) => {
              const rtl =
                getComputedStyle(event.currentTarget).direction === 'rtl';
              const target = segmented_target_index(
                event.key,
                index,
                options.length,
                rtl,
              );
              if (target === null) return;
              event.preventDefault();
              select(target);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
